/**
 * SearchableSelect - Generic searchable dropdown component with Portal rendering
 * Created to provide reusable searchable selection functionality across the app
 *
 * Features:
 * - Portal rendering for proper z-index management in modals
 * - Smart positioning (auto-adjusts based on available space)
 * - Search bar for filtering options
 * - Custom rendering support for options
 * - Click outside to close
 * - Escape key to close
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { Z_DROPDOWN_MENU_TOP } from '../../lib/zIndex';
import { getPortalRoot } from '../../lib/portalUtils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  renderOption?: (option: SelectOption) => React.ReactNode;
  renderValue?: (option: SelectOption) => React.ReactNode;
  filterFn?: (option: SelectOption, query: string) => boolean;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Sélectionner...',
  searchPlaceholder = 'Rechercher...',
  disabled = false,
  className = '',
  renderOption,
  renderValue,
  filterFn,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  // Default filter function
  const defaultFilterFn = (option: SelectOption, query: string) => {
    return option.label.toLowerCase().includes(query.toLowerCase());
  };

  const filterFunction = filterFn || defaultFilterFn;

  // Filter options based on search query
  const filteredOptions = searchQuery
    ? options.filter(option => filterFunction(option, searchQuery))
    : options;

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const dropdownHeight = 350; // Max height estimate (including search bar)

      setDropdownPosition(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset search query when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const defaultRenderOption = (option: SelectOption) => (
    <span>{option.label}</span>
  );

  const defaultRenderValue = (option: SelectOption) => (
    <span>{option.label}</span>
  );

  const renderOptionContent = renderOption || defaultRenderOption;
  const renderValueContent = renderValue || defaultRenderValue;

  const dropdownContent = isOpen && triggerRef.current && (
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
      style={{
        zIndex: Z_DROPDOWN_MENU_TOP,
        left: `${triggerRef.current.getBoundingClientRect().left}px`,
        [dropdownPosition === 'bottom' ? 'top' : 'bottom']: dropdownPosition === 'bottom'
          ? `${triggerRef.current.getBoundingClientRect().bottom + 4}px`
          : `${window.innerHeight - triggerRef.current.getBoundingClientRect().top + 4}px`,
        width: `${triggerRef.current.offsetWidth}px`,
        maxHeight: '350px',
      }}
    >
      {/* Search bar */}
      <div className="p-2 border-b border-gray-200 bg-gray-50">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Options list */}
      <div className="max-h-[300px] overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">
            {searchQuery ? 'Aucun résultat' : 'Aucune option disponible'}
          </div>
        ) : (
          filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={option.disabled}
              onClick={() => !option.disabled && handleSelect(option.value)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                option.value === value ? 'bg-primary-50 text-primary-600' : ''
              } ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {renderOptionContent(option)}
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-colors text-sm ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${className}`}
      >
        <span className="flex-1 text-left">
          {selectedOption ? renderValueContent(selectedOption) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {dropdownContent && createPortal(dropdownContent, getPortalRoot())}
    </>
  );
}
